import { formatCurrency } from '@if/core/utils'
import { ActionButton, ActionLink, Banner, Caption, FieldValue, Icon, StrongValue } from '@if/mui'
import {
  Box,
  Card,
  Checkbox,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import {
  useGetBankJobLines,
  type BankJobLine,
  type BankJobLenderPaymentDto,
} from '@src/api/lender-payment/bank-jobs-api'
import { useCompany } from '@src/services/company-hooks'
import { useManageMerchantsBaseUrl } from '@src/services/manage-merchants-hook'
import { TriangleAlert, Undo2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const PAYMENTS_PER_PAGE = 10

// Every line's table shares these widths, the description taking what is left, so the columns
// line up from one card to the next. Below its minimum width the table scrolls instead.
const PAYMENT_COLUMN_WIDTHS = { client: '25%', type: '15%', amount: 160 } as const
const PAYMENT_TABLE_MIN_WIDTH = 720

// The `xs` step of the design system's icon scale. The slot is held open whether or not a payment
// failed, so every customer name starts on the same column.
const NOTICE_SLOT_WIDTH = 14

interface Props {
  bankJobId: number
}

// The body of a file's detail page: one card per bank job line, the bank's record of one office
// payment, each pickable for a deposit return and listing the payments it carried.
const BankJobLines = ({ bankJobId }: Props) => {
  const { t } = useTranslation()
  const company = useCompany()
  const { data: lines, isLoading, isError } = useGetBankJobLines(company, bankJobId)
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set())
  const manageMerchantsBaseUrl = useManageMerchantsBaseUrl()

  const toggle = (key: string) =>
    setSelectedKeys((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        {/* TODO: the deposit-return flow on the picked lines is not specified yet. */}
        <ActionButton startIcon={Undo2}>
          {selectedKeys.size > 0
            ? t('disburse.history.depositReturnCount', { count: selectedKeys.size })
            : t('disburse.history.depositReturn')}
        </ActionButton>
      </Stack>

      {isLoading && <Skeleton variant="rounded" height={160} />}
      {isError && <Banner severity="error">{t('disburse.history.officesLoadError')}</Banner>}
      {lines?.map((line) => (
        <LineCard
          key={line.key}
          line={line}
          // manage-merchants keys its page on the Merchants API id, which is the payee id as is.
          merchantPageUrl={
            line.isKnownMerchant ? `${manageMerchantsBaseUrl}/merchant/${encodeURIComponent(line.payeeId)}/view` : null
          }
          selected={selectedKeys.has(line.key)}
          onToggle={() => toggle(line.key)}
        />
      ))}
    </Stack>
  )
}

interface LineCardProps {
  line: BankJobLine
  /** The payee's page in manage-merchants, or null when it has none: its name then stays plain text. */
  merchantPageUrl: string | null
  selected: boolean
  onToggle: () => void
}

// A line's header — its box picks it for a deposit return — over the payments it carried, ten to
// a page.
const LineCard = ({ line, merchantPageUrl, selected, onToggle }: LineCardProps) => {
  const { t } = useTranslation()
  const company = useCompany()
  const [page, setPage] = useState(0)
  const payments = line.payments.slice(page * PAYMENTS_PER_PAGE, (page + 1) * PAYMENTS_PER_PAGE)

  // What a failed payment's triangle says: a cancelled one names who and when, as the Monolith does,
  // when the cancel stamp carries both; a bank return shows the bank's code.
  const failureMessage = (payment: BankJobLenderPaymentDto) => {
    if (!payment.isCancelled) return t('disburse.history.paymentReturnedByBank', { code: payment.bankFailureCode })
    return payment.cancelledByFullName && payment.cancelledOn
      ? t('disburse.history.paymentCancelledBy', { user: payment.cancelledByFullName, date: payment.cancelledOn })
      : t('disburse.history.paymentCancelled')
  }

  return (
    <Card>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5 }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={onToggle}
          slotProps={{ input: { 'aria-label': t('disburse.history.selectOffice', { office: line.name }) } }}
        />
        {merchantPageUrl ? (
          <ActionLink href={merchantPageUrl} target="_blank">
            {line.name}
          </ActionLink>
        ) : (
          <StrongValue>{line.name}</StrongValue>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {/* The amount keeps its full width; a long payee name wraps instead. */}
        <StrongValue sx={{ px: 1, flexShrink: 0, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {formatCurrency(line.amount)}
        </StrongValue>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: PAYMENT_TABLE_MIN_WIDTH }}>
          <TableHead>
            <TableRow
              sx={(theme) => ({
                backgroundColor: theme.vars.palette.tableHead.bg,
                '& .MuiTableCell-root': { color: theme.vars.palette.tableHead.fg },
              })}
            >
              <TableCell sx={{ width: PAYMENT_COLUMN_WIDTHS.client }}>
                {t('disburse.history.paymentColumns.client')}
              </TableCell>
              <TableCell sx={{ width: PAYMENT_COLUMN_WIDTHS.type }}>
                {t('disburse.history.paymentColumns.type')}
              </TableCell>
              <TableCell>{t('disburse.history.paymentColumns.description')}</TableCell>
              <TableCell align="right" sx={{ width: PAYMENT_COLUMN_WIDTHS.amount }}>
                {t('disburse.history.paymentColumns.amount')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.operationId}>
                {/* Opens the customer's loan in a new tab, as the "Déboursé" tab does. A long name
                    wraps within its column rather than spill into the next one. */}
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box component="span" sx={{ display: 'inline-flex', width: NOTICE_SLOT_WIDTH, flexShrink: 0 }}>
                      {payment.isFailed && (
                        // The span carries the tooltip's ref, which the icon itself does not.
                        <Tooltip title={failureMessage(payment)}>
                          <Box component="span" sx={{ display: 'inline-flex' }}>
                            <Icon icon={TriangleAlert} size="xs" color="error" />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    <ActionLink href={`/${company}/loans/${payment.loanId}`} target="_blank">
                      {payment.borrowerFullName}
                    </ActionLink>
                  </Stack>
                </TableCell>
                <TableCell>
                  <FieldValue>
                    {payment.isAccountToAccountPayout
                      ? t('disburse.history.paymentTypes.accountToAccount')
                      : t('disburse.history.paymentTypes.disbursement')}
                  </FieldValue>
                </TableCell>
                {/* The Monolith's own sentence, verbatim — nothing is composed or reworded here. */}
                <TableCell>
                  <Caption>{payment.description}</Caption>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  <FieldValue>{formatCurrency(payment.amount)}</FieldValue>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {line.payments.length > PAYMENTS_PER_PAGE && (
        <TablePagination
          component="div"
          count={line.payments.length}
          page={page}
          onPageChange={(_event, next) => setPage(next)}
          rowsPerPage={PAYMENTS_PER_PAGE}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => t('disburse.history.displayedRows', { from, to, count })}
        />
      )}
    </Card>
  )
}

export default BankJobLines
